import type { BusinessDate } from '../date/business-date';
import { parseShifts, type ShiftWeather } from './daily-report-weather';
import { parseReportNotes, parseReportTasks, type DailyReportTask } from './daily-report-task';
import {
  parseCrewCounts,
  parseEquipment,
  type CrewCount,
  type EquipmentLine,
} from './daily-report-crew';
import { domainError, type DomainError } from '../shared/domain-error';
import { err, ok, type Result } from '../shared/result';
import type { DailyReportId, ProjectId, UserId } from '../shared/ids';
import { PLANNING_ERROR, PLANNING_ERROR_MESSAGE } from './errors';

/**
 * Relatório Diário de Obra — o dia visto de cima.
 *
 * A conferência é **por pessoa** (RN-100): num dia de quatorze funcionários há quatorze
 * conferências, cada uma com seu status e seu histórico. O RDO é **por obra** — um
 * documento, um clima, uma observação, uma assinatura. São granularidades diferentes, e é
 * por isso que ele não pôde ser a conferência com mais campos: o clima do dia não cabe em
 * quatorze linhas sem que se eleja uma delas como principal, e sem que o documento suma no
 * dia em que essa for reaberta (ADR-121).
 *
 * Esta entidade guarda **só o que é do dia inteiro**. O que já está na conferência
 * continua lá e é lido de lá — duplicar criaria dois lugares para a mesma verdade, e o
 * segundo é sempre o que fica desatualizado.
 *
 * ```text
 * RDO nº 142 · rev. 2          Obra: SPE - Batista de Castro
 * 10/09/2026, quinta-feira     Emitido por: Douglas Arvelos
 * ```
 *
 * ## Os três estados, e por que o terceiro existe
 *
 * ```text
 *            emitir                 o dia mudou por baixo
 *   RASCUNHO ──────▶ EMITIDO ──────────────────────────▶ DESATUALIZADO
 *                       ▲            abrir revisão            │
 *                       └───────────────────────────────────  ┘
 *                                    emitir de novo
 * ```
 *
 * Documento de obra vale pelo que estava escrito quando foi assinado. Mas a conferência é
 * reabrível por desenho (RN-106), e um ajuste três dias depois é o caso que o sistema
 * existe para permitir. Congelar o RDO faria o sistema divergir do papel em silêncio;
 * deixá-lo sempre refletir o agora faria dois PDFs do mesmo dia saírem diferentes sem
 * explicação. `OUTDATED` é a terceira saída: o que foi entregue continua valendo, e a
 * diferença fica **visível** em vez de acontecer por baixo (RN-210).
 */

export type DailyReportStatus = 'DRAFT' | 'CLOSED' | 'OUTDATED';

export const DAILY_REPORT_STATUS_LABEL: Readonly<Record<DailyReportStatus, string>> = {
  DRAFT: 'Rascunho',
  CLOSED: 'Emitido',
  OUTDATED: 'Desatualizado',
};

export interface DailyReport {
  readonly id: DailyReportId;
  readonly projectId: ProjectId;
  /** `YYYY-MM-DD`, o dia relatado. */
  readonly date: string;
  readonly status: DailyReportStatus;
  /**
   * O número do documento, sequencial por obra (RN-209).
   *
   * Ausente enquanto rascunho: número só existe depois que o documento saiu. Uma vez
   * atribuído, **não muda mais** — a revisão troca o `revision`, nunca o número, porque é
   * pelo número que quem recebeu o papel encontra o que está revendo.
   */
  readonly number?: number;
  /** Quantas vezes foi emitido. Zero enquanto rascunho; 1 na primeira emissão. */
  readonly revision: number;
  readonly closedByUserId?: UserId;
  readonly closedAt?: string;
  /** Quando o dia passou a divergir do que foi emitido (RN-210). */
  readonly outdatedAt?: string;
  /**
   * O tempo e a jornada, turno a turno (fatia 2).
   *
   * Guarda **só os turnos relatados** (RN-211): três linhas em branco para dizer "nada a
   * dizer" encheriam o banco de ausência, e toda leitura teria de filtrá-las de novo.
   */
  readonly shifts: readonly ShiftWeather[];
  /**
   * As tarefas que não são frente do cronograma (RN-213, fatia 3).
   *
   * As frentes **não** moram aqui: elas são derivadas das conferências do dia, e copiá-las
   * criaria dois lugares para a mesma verdade — o segundo sendo o que fica desatualizado
   * no primeiro ajuste.
   */
  readonly tasks: readonly DailyReportTask[];
  /**
   * O efetivo **corrigido** por função (RN-212).
   *
   * Só o que alguém digitou. O contado sai das conferências a cada leitura e não mora
   * aqui — derivado se deriva, pela mesma razão das frentes (ADR-122).
   */
  readonly crew: readonly CrewCount[];
  /** O equipamento do dia. Herdado do último RDO emitido da obra ao abrir (ADR-123). */
  readonly equipment: readonly EquipmentLine[];
  /** A observação do dia. Mora aqui, e não em `DailyInspection.notes`, que é por pessoa. */
  readonly notes?: string;
  readonly version: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

/** Chave natural: um RDO por obra e por dia — RN-207. */
export function dailyReportNaturalKey(projectId: ProjectId, date: string): string {
  return `${projectId}|${date}`;
}

/**
 * Não se relata o futuro (RN-208).
 *
 * O "hoje" é o da obra, pelo fuso dela (RN-123), pelo mesmo motivo da RN-102: emitir o RDO
 * de amanhã porque o servidor virou a data antes do canteiro é assinar um documento sobre
 * um dia que não aconteceu.
 */
export function ensureReportNotFuture(
  date: BusinessDate,
  today: BusinessDate,
): Result<void, DomainError> {
  if (date.isAfter(today)) {
    return err(
      domainError(
        PLANNING_ERROR.REPORT_IN_FUTURE,
        PLANNING_ERROR_MESSAGE[PLANNING_ERROR.REPORT_IN_FUTURE]!,
        { date: date.toString(), today: today.toString() },
      ),
    );
  }
  return ok(undefined);
}

export function openDailyReport(input: {
  id: DailyReportId;
  projectId: ProjectId;
  date: string;
  now: string;
  /**
   * O equipamento do último RDO emitido da obra (ADR-123).
   *
   * O canteiro tem as mesmas máquinas hoje e ontem, e digitar cinco linhas idênticas
   * duzentas e cinquenta vezes por ano é o que faz alguém parar de preencher a seção. Quem
   * relata corrige o que mudou. Vem do último **emitido**, e não do último qualquer: um
   * rascunho abandonado propagaria linhas que ninguém confirmou.
   */
  equipment?: readonly EquipmentLine[];
}): DailyReport {
  return {
    id: input.id,
    projectId: input.projectId,
    date: input.date,
    status: 'DRAFT',
    revision: 0,
    shifts: [],
    tasks: [],
    crew: [],
    equipment: input.equipment ?? [],
    version: 1,
    createdAt: input.now,
    updatedAt: input.now,
  };
}

/** Emitido alguma vez — ou seja, já tem número e já saiu daqui. */
export function wasIssued(report: DailyReport): boolean {
  return report.number !== undefined;
}

/** Aceita alteração de conteúdo. Emitido não aceita: abra revisão antes (RN-210). */
export function isEditable(report: DailyReport): boolean {
  return report.status !== 'CLOSED';
}

/**
 * Pode virar arquivo (RN-217).
 *
 * Só o emitido **e em dia**, e as duas metades da frase custam caro se forem afrouxadas:
 *
 * - **o rascunho não tem número**, e um arquivo chamado `RDO-0000` circulando por e-mail
 *   é um documento sem identidade se passando por um. Emitir é um toque, e é o ato que
 *   dá o número (RN-209);
 * - **o desatualizado tem número, e ele mente.** Gerar o PDF de um `OUTDATED` produziria
 *   um papel com o cabeçalho da emissão anterior e o conteúdo de agora — que é exatamente
 *   a divergência silenciosa que o estado existe para impedir (ADR-122). Quem quiser o
 *   papel atual emite a revisão, e aí o cabeçalho volta a dizer a verdade.
 *
 * O preço é não haver pré-visualização do rascunho. É pequeno perto de um PDF que sai
 * diferente do que diz ser.
 */
export function isRenderable(report: DailyReport): boolean {
  return report.status === 'CLOSED';
}

/**
 * A guarda de toda escrita de conteúdo.
 *
 * Uma só, e por isso uma função: o documento emitido recusa clima, tarefa e observação
 * pela mesma razão, e três cópias da mesma verificação são três chances de a quarta
 * escrita nascer sem ela.
 */
function ensureReportEditable(report: DailyReport): Result<void, DomainError> {
  if (isEditable(report)) return ok(undefined);
  return err(
    domainError(
      PLANNING_ERROR.REPORT_ALREADY_CLOSED,
      PLANNING_ERROR_MESSAGE[PLANNING_ERROR.REPORT_ALREADY_CLOSED]!,
      { number: report.number },
    ),
  );
}

/**
 * Emite o relatório (RN-209).
 *
 * `nextNumber` é o próximo número da obra, e quem o calcula é o caso de uso — o domínio
 * não sabe quantos RDOs a obra já emitiu. Ele só é usado na **primeira** emissão: da
 * segunda em diante o número é o que já estava, e o que sobe é a revisão.
 *
 * Emitir limpa `outdatedAt`: o documento voltou a ser o retrato do dia.
 */
export function closeDailyReport(
  report: DailyReport,
  nextNumber: number,
  by: UserId,
  now: string,
): Result<DailyReport, DomainError> {
  if (report.status === 'CLOSED') {
    return err(
      domainError(
        PLANNING_ERROR.REPORT_ALREADY_CLOSED,
        PLANNING_ERROR_MESSAGE[PLANNING_ERROR.REPORT_ALREADY_CLOSED]!,
        { number: report.number },
      ),
    );
  }

  const numero = report.number ?? nextNumber;
  if (!Number.isInteger(numero) || numero < 1) {
    return err(
      domainError(
        PLANNING_ERROR.REPORT_NUMBER_INVALID,
        PLANNING_ERROR_MESSAGE[PLANNING_ERROR.REPORT_NUMBER_INVALID]!,
        { number: numero },
      ),
    );
  }

  const { outdatedAt: _desatualizado, ...rest } = report;

  return ok({
    ...rest,
    status: 'CLOSED',
    number: numero,
    revision: report.revision + 1,
    closedByUserId: by,
    closedAt: now,
    version: report.version + 1,
    updatedAt: now,
  });
}

/**
 * O dia mudou por baixo de um documento já emitido (RN-210).
 *
 * **Total de propósito, e devolve o mesmo objeto quando não há o que fazer.** Quem chama
 * isto é o efeito colateral de reabrir uma conferência, e reabrir a segunda conferência do
 * mesmo dia não pode falhar porque a primeira já desatualizou o RDO. Rascunho também
 * atravessa intacto: não há o que desatualizar em documento que nunca saiu.
 *
 * Quem chama pode comparar por identidade — `atualizado !== relatorio` — para saber se
 * precisa gravar.
 */
export function markReportOutdated(report: DailyReport, now: string): DailyReport {
  if (report.status !== 'CLOSED') return report;

  return {
    ...report,
    status: 'OUTDATED',
    outdatedAt: now,
    version: report.version + 1,
    updatedAt: now,
  };
}

/**
 * Abre revisão de um documento emitido — o caminho manual para o mesmo estado.
 *
 * Diferente de `markReportOutdated` em duas coisas, e as duas importam: é **ato de
 * alguém**, então recusa quando não há o que revisar em vez de atravessar em silêncio; e
 * existe para a correção que ninguém detecta sozinho — o clima digitado errado, a
 * observação que ficou pela metade. O estado é o mesmo porque a consequência é a mesma: o
 * que está aqui deixou de ser o que foi entregue.
 */
export function reopenDailyReportForRevision(
  report: DailyReport,
  now: string,
): Result<DailyReport, DomainError> {
  if (report.status !== 'CLOSED') {
    return err(
      domainError(
        PLANNING_ERROR.REPORT_NOT_CLOSED,
        PLANNING_ERROR_MESSAGE[PLANNING_ERROR.REPORT_NOT_CLOSED]!,
        { status: report.status },
      ),
    );
  }

  return ok(markReportOutdated(report, now));
}

/** `RDO nº 142` ou `RDO nº 142 · rev. 2` — o rascunho ainda não tem o que mostrar. */
export function describeDailyReport(report: DailyReport): string {
  if (report.number === undefined) return 'RDO (rascunho)';
  const numero = `RDO nº ${String(report.number)}`;
  return report.revision > 1 ? `${numero} · rev. ${String(report.revision)}` : numero;
}

/**
 * Grava o tempo e a jornada do dia (fatia 2).
 *
 * É a **primeira** alteração de conteúdo que o documento aceita, e portanto o primeiro
 * lugar onde `isEditable` deixa de ser enfeite: um documento emitido recusa, e quem quiser
 * corrigi-lo abre revisão antes (RN-210). Sem isto, o papel entregue e o que está na tela
 * passariam a divergir sem que nada registrasse quando — que é justamente o que o estado
 * `OUTDATED` existe para impedir.
 */
export function setDailyReportWeather(
  report: DailyReport,
  shifts: readonly ShiftWeather[],
  now: string,
): Result<DailyReport, DomainError> {
  const aberto = ensureReportEditable(report);
  if (!aberto.ok) return err(aberto.error);

  const validados = parseShifts(shifts);
  if (!validados.ok) return err(validados.error);

  return ok({
    ...report,
    shifts: validados.value,
    version: report.version + 1,
    updatedAt: now,
  });
}

/**
 * Grava as tarefas avulsas do dia (RN-213, fatia 3).
 *
 * Substitui a lista inteira, e não acrescenta: é o mesmo tratamento dos turnos, e o mesmo
 * de `openings` na parede (`0012`). A tela edita a tabela toda e manda a tabela toda —
 * acrescentar obrigaria a inventar identidade para linha que não tem vida própria.
 */
export function setDailyReportTasks(
  report: DailyReport,
  tasks: readonly DailyReportTask[],
  now: string,
): Result<DailyReport, DomainError> {
  const aberto = ensureReportEditable(report);
  if (!aberto.ok) return err(aberto.error);

  const validadas = parseReportTasks(tasks);
  if (!validadas.ok) return err(validadas.error);

  return ok({
    ...report,
    tasks: validadas.value,
    version: report.version + 1,
    updatedAt: now,
  });
}

/**
 * Grava a observação do dia.
 *
 * É o campo que no RDO de referência carrega o que não cabe em nenhuma tabela: quem
 * passou mal e foi embora às 08:30, a entrega que chegou às 08:10, o empreiteiro que
 * apareceu. Texto vazio **apaga** a observação em vez de gravar string vazia — ausência e
 * "escreveram nada" são a mesma coisa aqui, e representá-las de dois jeitos daria duas
 * telas diferentes para o mesmo dia.
 */
export function setDailyReportNotes(
  report: DailyReport,
  notes: string,
  now: string,
): Result<DailyReport, DomainError> {
  const aberto = ensureReportEditable(report);
  if (!aberto.ok) return err(aberto.error);

  const validada = parseReportNotes(notes);
  if (!validada.ok) return err(validada.error);

  const { notes: _anterior, ...rest } = report;

  return ok({
    ...rest,
    ...(validada.value === undefined ? {} : { notes: validada.value }),
    version: report.version + 1,
    updatedAt: now,
  });
}

/** Grava o efetivo corrigido por função (RN-212). Substitui a lista inteira. */
export function setDailyReportCrew(
  report: DailyReport,
  crew: readonly CrewCount[],
  now: string,
): Result<DailyReport, DomainError> {
  const aberto = ensureReportEditable(report);
  if (!aberto.ok) return err(aberto.error);

  const validado = parseCrewCounts(crew);
  if (!validado.ok) return err(validado.error);

  return ok({
    ...report,
    crew: validado.value,
    version: report.version + 1,
    updatedAt: now,
  });
}

/** Grava o equipamento do dia (ADR-123). Substitui a lista inteira. */
export function setDailyReportEquipment(
  report: DailyReport,
  equipment: readonly EquipmentLine[],
  now: string,
): Result<DailyReport, DomainError> {
  const aberto = ensureReportEditable(report);
  if (!aberto.ok) return err(aberto.error);

  const validado = parseEquipment(equipment);
  if (!validado.ok) return err(validado.error);

  return ok({
    ...report,
    equipment: validado.value,
    version: report.version + 1,
    updatedAt: now,
  });
}
